import { MutableRefObject, useState } from 'react'
import styles from '../TechPartnerDashboard/TechPartnerDashboard.module.scss'
import SalesQuotaData from './SectionData/SalesQuotaData'
import MarketOpportunityData from './SectionData/MarketOpportunityData'
import TeamResourcesData from './SectionData/TeamResourcesData'
import SalesProcessData from './SectionData/SalesProcessData'
import FulfillmentProcessData from './SectionData/FulfillmentProcessData'
import CustomersData from './SectionData/CustomersData'
import { TechPartnerDashboardInput } from '../TechPartnerDashboard/TechPartnerDashboard'
import html2canvas from 'html2canvas'
import { fqAlert } from '../../GlobalComponents/NotificationManager/NotificationManager'
import { SalesDivision, Salesperson } from '../../features/salesperson/salespersonAPI'

export enum TechPartnerDashboardSectionType {
    SALESQUOTA = 'Sales Plan',
    MARKETOPPORTUNITY = 'Market Opportunity',
    TEAMRESOURCES = 'Team Resources',
    SALESPROCESS = 'Sales Process',
    FULFILLMENTPROCESS = 'Fulfillment Process',
    CUSTOMERS = 'Customers',
}

interface TechPartnerDashboardSectionProps {
    type: TechPartnerDashboardSectionType
    selectedInputs: TechPartnerDashboardInput
    selectedSalespeople?: Salesperson[]
    selectedSalesDivisions?: SalesDivision[]
    sectionRef: MutableRefObject<null>
}

export interface TechPartnerDashboardDataSectionProps {
    type: TechPartnerDashboardSectionType
    selectedInputs: TechPartnerDashboardInput
    selectedSalespeople?: Salesperson[]
    selectedSalesDivisions?: SalesDivision[]
    loading: boolean
    onLoadingChange(loading: boolean): void
    onTitleBarTextChange?(text: string | JSX.Element): void
    onTitleBarElementChange?(element: JSX.Element): void
}

export const TechPartnerDashboardSection = ({
    type,
    selectedInputs,
    selectedSalespeople,
    selectedSalesDivisions,
    sectionRef,
}: TechPartnerDashboardSectionProps) => {
    const [loading, setLoading] = useState(true)
    const [titleBarText, setTitleBarText] = useState<string | JSX.Element>('')
    const [titleBarElement, setTitleBarElement] = useState(<div></div>)

    const copyToClipboard = async (pngBlob: Blob) => {
        try {
            await navigator.clipboard.write([
                new ClipboardItem({
                    [pngBlob.type]: pngBlob,
                }),
            ])
            fqAlert({ type: 'success', message: `${type} Section copied successfully!` })
        } catch (error) {
            console.error(error)
        }
    }

    const exportSection = async (section: Element) => {
        const canvas = await html2canvas(section as HTMLElement, {
            onclone: function (documentClone) {
                let sectionHeaderDisclaimerClone = documentClone.querySelector(
                    `div[data-section-name='${type}'] .${styles.headerDisclaimer}`
                ) as HTMLDivElement | null
                if (sectionHeaderDisclaimerClone)
                    sectionHeaderDisclaimerClone.innerText = 'Confidential - AWC Internal Only'
                documentClone
                    .querySelectorAll('.hide-on-export')
                    .forEach((el) => ((el as HTMLElement).style.visibility = 'hidden'))
            },
        })

        canvas.toBlob(
            async (blob) => {
                if (blob) await copyToClipboard(blob)
            },
            'image/png',
            1.0
        )
    }

    const onLoadingChange = (loading: boolean) => setLoading(loading)
    const onTitleBarTextChange = (text: string | JSX.Element) => setTitleBarText(text)
    const onTitleBarElementChange = (element: JSX.Element) => setTitleBarElement(element)

    const DataSection = (dataSectionsProps: TechPartnerDashboardSectionProps) => {
        if (type === TechPartnerDashboardSectionType.SALESQUOTA)
            return (
                <SalesQuotaData
                    type={dataSectionsProps.type}
                    selectedInputs={dataSectionsProps.selectedInputs}
                    onLoadingChange={onLoadingChange}
                    loading={loading}
                    selectedSalespeople={selectedSalespeople}
                    selectedSalesDivisions={selectedSalesDivisions}
                />
            )
        if (type === TechPartnerDashboardSectionType.MARKETOPPORTUNITY)
            return (
                <MarketOpportunityData
                    type={dataSectionsProps.type}
                    selectedInputs={dataSectionsProps.selectedInputs}
                    onLoadingChange={onLoadingChange}
                    loading={loading}
                />
            )
        if (type === TechPartnerDashboardSectionType.TEAMRESOURCES)
            return (
                <TeamResourcesData
                    type={dataSectionsProps.type}
                    selectedInputs={dataSectionsProps.selectedInputs}
                    onLoadingChange={onLoadingChange}
                    onTitleBarTextChange={onTitleBarTextChange}
                    loading={loading}
                />
            )
        if (type === TechPartnerDashboardSectionType.SALESPROCESS)
            return (
                <SalesProcessData
                    type={dataSectionsProps.type}
                    selectedInputs={dataSectionsProps.selectedInputs}
                    onLoadingChange={onLoadingChange}
                    loading={loading}
                />
            )
        if (type === TechPartnerDashboardSectionType.FULFILLMENTPROCESS)
            return (
                <FulfillmentProcessData
                    type={dataSectionsProps.type}
                    selectedInputs={dataSectionsProps.selectedInputs}
                    onLoadingChange={onLoadingChange}
                    loading={loading}
                />
            )
        if (type === TechPartnerDashboardSectionType.CUSTOMERS)
            return (
                <CustomersData
                    type={dataSectionsProps.type}
                    selectedInputs={dataSectionsProps.selectedInputs}
                    onLoadingChange={onLoadingChange}
                    onTitleBarTextChange={onTitleBarTextChange}
                    onTitleBarElementChange={onTitleBarElementChange}
                    loading={loading}
                />
            )
        return <></>
    }

    return (
        <div style={{ position: 'relative' }}>
            {loading && (
                <div
                    className={styles.app}
                    style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        zIndex: 10,
                    }}
                >
                    <div className={'centered'} style={{ height: '100%' }}>
                        <div
                            className={'k-i-loading'}
                            style={{ fontSize: 100, height: '100px', top: '27px' }}
                        />
                    </div>
                </div>
            )}
            <div className={styles.sectionContainer} data-section-name={type} ref={sectionRef}>
                <div className={`${styles.sectionTitle}`}>
                    {type}
                    <button
                        className={`${styles.exportButton} hide-on-export`}
                        onClick={(e) => {
                            let elSection = (e.target as Element).closest(
                                `.${styles.sectionContainer}`
                            )
                            if (elSection) exportSection(elSection as Element)
                        }}
                    >
                        Copy
                    </button>
                    {titleBarText && <div className={styles.titleBarText}>{titleBarText}</div>}
                    <div className={styles.headerDisclaimer}></div>
                    {titleBarElement && (
                        <div className={`${styles.titleBarElement} hide-on-export`}>
                            {titleBarElement}
                        </div>
                    )}
                </div>
                {DataSection({
                    type: type,
                    selectedInputs: selectedInputs,
                    sectionRef: sectionRef,
                })}
            </div>
        </div>
    )
}

export default TechPartnerDashboardSection
